import { useEffect, useState, useCallback } from 'react';
import { useAdmin } from '@/hooks/useAdmin';
import { Inbox, Loader2, CheckCheck, Archive } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TOPIC_LABELS = {
  editorial: 'Uredništvo',
  advertising: 'Oglašavanje',
  collaboration: 'Suradnja',
  other: 'Ostalo',
};

export default function ContactMessages() {
  const { getAccessTokenSilently } = useAdmin();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');

  const load = useCallback(async () => {
    const token = await getAccessTokenSilently();
    const q = filter ? `?status=${filter}` : '';
    const r = await fetch(`${API}/admin/contact-messages${q}`, { headers: { Authorization: `Bearer ${token}` } });
    if (r.ok) setMessages((await r.json()).messages || []);
    setLoading(false);
  }, [filter, getAccessTokenSilently]);

  useEffect(() => { load(); }, [load]);

  async function setStatus(id, status) {
    const token = await getAccessTokenSilently();
    const r = await fetch(`${API}/admin/contact-messages/${id}`, {
      method: 'PATCH', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ status }),
    });
    if (r.ok) { toast.success('Ažurirano'); load(); }
  }

  if (loading) return <div className="p-10 flex items-center justify-center"><Loader2 className="animate-spin text-zinc-500" /></div>;

  return (
    <div className="p-6" data-testid="contact-messages-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2"><Inbox size={22} /> Kontakt poruke</h1>
          <p className="text-sm text-zinc-500">{messages.length} poruka</p>
        </div>
        <select value={filter} onChange={e => setFilter(e.target.value)} className="px-3 py-2 bg-zinc-800 border border-zinc-700 rounded text-sm text-white" data-testid="msg-filter">
          <option value="">Sve</option>
          <option value="new">Nove</option>
          <option value="read">Pročitane</option>
          <option value="archived">Arhivirane</option>
        </select>
      </div>

      <div className="space-y-3">
        {messages.map(m => (
          <div key={m.id} className={`bg-zinc-900 border rounded-xl p-5 ${m.status === 'new' ? 'border-red-600/40' : 'border-zinc-800'}`} data-testid={`msg-${m.id}`}>
            <div className="flex items-start justify-between mb-2">
              <div>
                <p className="text-white font-semibold">{m.first_name} {m.last_name} <span className="text-zinc-500 font-normal">&lt;{m.email}&gt;</span></p>
                <div className="flex items-center gap-3 text-xs text-zinc-500 mt-1">
                  <span className="px-2 py-0.5 bg-zinc-800 rounded">{TOPIC_LABELS[m.topic] || m.topic}</span>
                  <span>{new Date(m.created_at).toLocaleString('hr')}</span>
                  {m.status === 'new' && <span className="text-red-400 font-bold">• NEW</span>}
                </div>
              </div>
              <div className="flex gap-2">
                {m.status !== 'read' && <button onClick={() => setStatus(m.id, 'read')} className="text-xs text-emerald-400 hover:text-emerald-300 flex items-center gap-1"><CheckCheck size={12} /> Pročitano</button>}
                {m.status !== 'archived' && <button onClick={() => setStatus(m.id, 'archived')} className="text-xs text-zinc-400 hover:text-white flex items-center gap-1"><Archive size={12} /> Arhiviraj</button>}
              </div>
            </div>
            <p className="text-sm text-zinc-300 whitespace-pre-wrap">{m.message}</p>
          </div>
        ))}
        {messages.length === 0 && <p className="text-center py-12 text-zinc-500">Nema poruka.</p>}
      </div>
    </div>
  );
}
